import React, { useState, useEffect } from "react";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setBalance } from "../store/balance/balanceSlice";
import { formatDateToYYYYMMDD } from "../../helpers/DateHelpers";
import { CircularProgress } from "@mui/material";
import RcsBulkMessageModal from "../RcsBulkMessage/RcsBulkMessageModal";
import Swal from "sweetalert2";

export default function RCSCarouselPage({ activeTab, fetchData }) {
  const loggedInUserName = sessionStorage.getItem("username");
  const { rcsAgentId } = useSelector((state) => state.auth);

  const initialFormState = {
    username: "",
    campaignID: "",
    contacts: "",
    cards: [
      {
        cardTitle: "",
        cardDescription: "",
        fileUrl: "",
        suggestionType:
          activeTab === "CAROUSEL" ? "openUrl" : "createCalendarEvent",
        buttonText: "",
        buttonUrl: "",
        calendarStartTime: "",
        calendarEndTime: "",
        calendarTitle: "",
        calendarDescription: "",
        locationLatitude: "",
        locationLongitude: "",
        locationLabel: "",
        dialerPhoneNumber: "",
        postBackData: "",
      },
    ],
  };

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [checking, setChecking] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [capabilityResult, setCapabilityResult] = useState("");
  const dispatch = useDispatch();

  const [verifiedPhones, setVerifiedPhones] = useState([]);
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [bulkFileName, setBulkFileName] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const [bulkType, setBulkType] = useState("");
  const [isNameRequired, setIsNameRequired] = useState(false);

  const MAX_CARDS = 10; // Maximum number of cards

  // Reset form when activeTab changes
  useEffect(() => {
    setForm({
      ...initialFormState,
      cards: [
        {
          ...initialFormState.cards[0],
          suggestionType:
            activeTab === "CAROUSEL" ? "openUrl" : "createCalendarEvent",
        },
      ],
    });
    setErrors({});
    setCapabilityResult("");
    setVerifiedPhones([]);
  }, [activeTab, bulkFileName]);

  useEffect(() => {
    setIsVerified(false);
    setIsBulkUpload(false);
    setBulkFileName(null);
  }, [activeTab]);

  const validateField = (name, value, cardIndex = null) => {
    const newErrors = {};
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/.*)?$/i;
    const repetitiveNumberPattern = /^(\d)\1{9}$/;
    const phonePattern = /^[6-9]\d{9}$/;
    const latitudePattern = /^-?([0-8]?[0-9](\.\d+)?|90(\.0+)?)$/;
    const longitudePattern = /^-?((1?[0-7]?[0-9](\.\d+)?)|180(\.0+)?)$/;
    const alphanumericPattern = /^[a-zA-Z0-9 _-]+$/;

    if (cardIndex !== null) {
      const cardErrors = errors.cards?.[cardIndex] || {};
      switch (name) {
        case "cardTitle":
          if (!value || value.trim() === "") {
            cardErrors.cardTitle = "Card Title is required";
          } else if (value.length < 2) {
            cardErrors.cardTitle = "Card Title must be at least 2 characters";
          } else if (value.length > 100) {
            cardErrors.cardTitle = "Card Title must be 100 characters or less";
          } else {
            cardErrors.cardTitle = "";
          }
          break;
        case "cardDescription":
          if (!value || value.trim() === "") {
            cardErrors.cardDescription = "Card Description is required";
          } else if (value.length < 2) {
            cardErrors.cardDescription =
              "Card Description must be at least 2 characters";
          } else if (value.length > 500) {
            cardErrors.cardDescription =
              "Card Description must be 500 characters or less";
          } else {
            cardErrors.cardDescription = "";
          }
          break;
        case "fileUrl":
          if (!value || value.trim() === "") {
            cardErrors.fileUrl = "Image URL is required";
          } else if (!urlPattern.test(value)) {
            cardErrors.fileUrl =
              "Please enter a valid URL starting with http:// or https://";
          } else {
            cardErrors.fileUrl = "";
          }
          break;
        case "buttonText":
          if (!value || value.trim() === "") {
            cardErrors.buttonText = "Button Text is required";
          } else if (value.length < 2) {
            cardErrors.buttonText = "Button Text must be at least 2 characters";
          } else if (value.length > 50) {
            cardErrors.buttonText = "Button Text must be 50 characters or less";
          } else {
            cardErrors.buttonText = "";
          }
          break;
        case "buttonUrl":
          if (!value || value.trim() === "") {
            cardErrors.buttonUrl = "Button URL is required";
          } else if (!urlPattern.test(value)) {
            cardErrors.buttonUrl =
              "Please enter a valid URL starting with http:// or https://";
          } else {
            cardErrors.buttonUrl = "";
          }
          break;
        case "calendarStartTime":
          if (!value || value.trim() === "") {
            cardErrors.calendarStartTime = "Start Time is required";
          } else {
            cardErrors.calendarStartTime = "";
          }
          break;
        case "calendarEndTime":
          if (!value || value.trim() === "") {
            cardErrors.calendarEndTime = "End Time is required";
          } else if (value <= form.cards[cardIndex].calendarStartTime) {
            cardErrors.calendarEndTime = "End Time must be after Start Time";
          } else {
            cardErrors.calendarEndTime = "";
          }
          break;
        case "calendarTitle":
          if (!value || value.trim() === "") {
            cardErrors.calendarTitle = "Calendar Title is required";
          } else if (value.length < 2) {
            cardErrors.calendarTitle =
              "Calendar Title must be at least 2 characters";
          } else if (value.length > 100) {
            cardErrors.calendarTitle =
              "Calendar Title must be 100 characters or less";
          } else {
            cardErrors.calendarTitle = "";
          }
          break;
        case "calendarDescription":
          if (!value || value.trim() === "") {
            cardErrors.calendarDescription = "Calendar Description is required";
          } else if (value.length < 2) {
            cardErrors.calendarDescription =
              "Calendar Description must be at least 2 characters";
          } else if (value.length > 500) {
            cardErrors.calendarDescription =
              "Calendar Description must be 500 characters or less";
          } else {
            cardErrors.calendarDescription = "";
          }
          break;
        case "locationLatitude":
          if (!value || value.trim() === "") {
            cardErrors.locationLatitude = "Latitude is required";
          } else if (!latitudePattern.test(value)) {
            cardErrors.locationLatitude =
              "Please enter a valid latitude (-90 to 90)";
          } else {
            cardErrors.locationLatitude = "";
          }
          break;
        case "locationLongitude":
          if (!value || value.trim() === "") {
            cardErrors.locationLongitude = "Longitude is required";
          } else if (!longitudePattern.test(value)) {
            cardErrors.locationLongitude =
              "Please enter a valid longitude (-180 to 180)";
          } else {
            cardErrors.locationLongitude = "";
          }
          break;
        case "locationLabel":
          if (!value || value.trim() === "") {
            cardErrors.locationLabel = "Location Label is required";
          } else if (value.length < 2) {
            cardErrors.locationLabel =
              "Location Label must be at least 2 characters";
          } else if (value.length > 100) {
            cardErrors.locationLabel =
              "Location Label must be 100 characters or less";
          } else {
            cardErrors.locationLabel = "";
          }
          break;
        case "dialerPhoneNumber":
          if (!value || value.trim() === "") {
            cardErrors.dialerPhoneNumber = "Phone Number is required";
          } else if (!phonePattern.test(value)) {
            cardErrors.dialerPhoneNumber =
              "Please enter a valid 10 digit number starting with either 6, 7, 8 or 9";
          } else if (repetitiveNumberPattern.test(value)) {
            cardErrors.dialerPhoneNumber =
              "Repetitive phone numbers are not allowed";
          } else {
            cardErrors.dialerPhoneNumber = "";
          }
          break;
        default:
          break;
      }
      return { cards: { ...errors.cards, [cardIndex]: cardErrors } };
    } else {
      switch (name) {
        case "campaignID":
          if (!value || value.trim() === "") {
            newErrors.campaignID = "Campaign ID is required";
          } else if (!alphanumericPattern.test(value)) {
            newErrors.campaignID =
              "Campaign ID must contain only letters and numbers";
          } else if (value.length < 3) {
            newErrors.campaignID = "Campaign ID must be at least 3 characters";
          } else if (value.length > 50) {
            newErrors.campaignID = "Campaign ID must be 50 characters or less";
          } else {
            newErrors.campaignID = "";
          }
          break;
        case "contacts":
          if (!isBulkUpload) {
            if (!value) {
              newErrors.contacts = "Contacts are required";
            } else {
              const contactsArray = value.split(",").map((c) => c.trim());
              if (contactsArray.some((c) => !phonePattern.test(c))) {
                newErrors.contacts =
                  "Please enter a valid 10 digit number starting with either 6, 7, 8 or 9";
              } else if (
                contactsArray.some((c) => repetitiveNumberPattern.test(c))
              ) {
                newErrors.contacts = "Repetitive phone numbers are not allowed";
              } else {
                newErrors.contacts = "";
              }
            }
          }
          break;
        default:
          break;
      }
      return newErrors;
    }
  };

  const validateForm = () => {
    let newErrors = {};
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/.*)?$/i;
    const repetitiveNumberPattern = /^(\d)\1{9}$/;
    const phonePattern = /^[6-9]\d{9}$/;
    const latitudePattern = /^-?([0-8]?[0-9](\.\d+)?|90(\.0+)?)$/;
    const longitudePattern = /^-?((1?[0-7]?[0-9](\.\d+)?)|180(\.0+)?)$/;
    const alphanumericPattern = /^[a-zA-Z0-9 _-]+$/;

    // Validate campaignID
    if (!form.campaignID || form.campaignID.trim() === "") {
      newErrors.campaignID = "Campaign ID is required";
    } else if (!alphanumericPattern.test(form.campaignID)) {
      newErrors.campaignID =
        "Campaign ID must contain only letters and numbers";
    } else if (form.campaignID.length < 3) {
      newErrors.campaignID = "Campaign ID must be at least 3 characters";
    } else if (form.campaignID.length > 50) {
      newErrors.campaignID = "Campaign ID must be 50 characters or less";
    }

    // Validate contacts
    if (!isBulkUpload) {
      if (!form.contacts) {
        newErrors.contacts = "Contacts are required";
      } else {
        const contactsArray = form.contacts.split(",").map((c) => c.trim());
        if (contactsArray.some((c) => !phonePattern.test(c))) {
          newErrors.contacts =
            "Please enter a valid 10 digit number starting with either 6, 7, 8 or 9";
        } else if (contactsArray.some((c) => repetitiveNumberPattern.test(c))) {
          newErrors.contacts = "Repetitive phone numbers are not allowed";
        }
      }
    }

    // Validate cards
    const cardErrors = {};
    form.cards.forEach((card, index) => {
      const cardError = {};
      if (!card.cardTitle || card.cardTitle.trim() === "") {
        cardError.cardTitle = "Card Title is required";
      } else if (card.cardTitle.length < 2) {
        cardError.cardTitle = "Card Title must be at least 2 characters";
      } else if (card.cardTitle.length > 100) {
        cardError.cardTitle = "Card Title must be 100 characters or less";
      }

      if (!card.cardDescription || card.cardDescription.trim() === "") {
        cardError.cardDescription = "Card Description is required";
      } else if (card.cardDescription.length < 2) {
        cardError.cardDescription =
          "Card Description must be at least 2 characters";
      } else if (card.cardDescription.length > 500) {
        cardError.cardDescription =
          "Card Description must be 500 characters or less";
      }
      if (bulkType === "name" && !/\{\{Name\}\}/.test(card.cardDescription)) {
        cardError.cardDescription =
          "Card Description must include {{Name}} placeholder for Bulk Name upload";
      }

      if (!card.fileUrl || card.fileUrl.trim() === "") {
        cardError.fileUrl = "Image URL is required";
      } else if (!urlPattern.test(card.fileUrl)) {
        cardError.fileUrl =
          "Please enter a valid URL starting with http:// or https://";
      }

      if (!card.buttonText || card.buttonText.trim() === "") {
        cardError.buttonText = "Button Text is required";
      } else if (card.buttonText.length < 2) {
        cardError.buttonText = "Button Text must be at least 2 characters";
      } else if (card.buttonText.length > 50) {
        cardError.buttonText = "Button Text must be 50 characters or less";
      }

      if (activeTab === "CAROUSEL" || card.suggestionType === "openUrl") {
        if (!card.buttonUrl || card.buttonUrl.trim() === "") {
          cardError.buttonUrl = "Button URL is required";
        } else if (!urlPattern.test(card.buttonUrl)) {
          cardError.buttonUrl =
            "Please enter a valid URL starting with http:// or https://";
        }
      }

      if (card.suggestionType === "createCalendarEvent") {
        if (!card.calendarStartTime || card.calendarStartTime.trim() === "") {
          cardError.calendarStartTime = "Start Time is required";
        }
        if (!card.calendarEndTime || card.calendarEndTime.trim() === "") {
          cardError.calendarEndTime = "End Time is required";
        } else if (card.calendarEndTime <= card.calendarStartTime) {
          cardError.calendarEndTime = "End Time must be after Start Time";
        }
        if (!card.calendarTitle || card.calendarTitle.trim() === "") {
          cardError.calendarTitle = "Calendar Title is required";
        } else if (card.calendarTitle.length < 2) {
          cardError.calendarTitle =
            "Calendar Title must be at least 2 characters";
        } else if (card.calendarTitle.length > 100) {
          cardError.calendarTitle =
            "Calendar Title must be 100 characters or less";
        }
        if (
          !card.calendarDescription ||
          card.calendarDescription.trim() === ""
        ) {
          cardError.calendarDescription = "Calendar Description is required";
        } else if (card.calendarDescription.length < 2) {
          cardError.calendarDescription =
            "Calendar Description must be at least 2 characters";
        } else if (card.calendarDescription.length > 500) {
          cardError.calendarDescription =
            "Calendar Description must be 500 characters or less";
        }
      }

      if (card.suggestionType === "showLocation") {
        if (!card.locationLatitude || card.locationLatitude.trim() === "") {
          cardError.locationLatitude = "Latitude is required";
        } else if (!latitudePattern.test(card.locationLatitude)) {
          cardError.locationLatitude =
            "Please enter a valid latitude (-90 to 90)";
        }
        if (!card.locationLongitude || card.locationLongitude.trim() === "") {
          cardError.locationLongitude = "Longitude is required";
        } else if (!longitudePattern.test(card.locationLongitude)) {
          cardError.locationLongitude =
            "Please enter a valid longitude (-180 to 180)";
        }
        if (!card.locationLabel || card.locationLabel.trim() === "") {
          cardError.locationLabel = "Location Label is required";
        } else if (card.locationLabel.length < 2) {
          cardError.locationLabel =
            "Location Label must be at least 2 characters";
        } else if (card.locationLabel.length > 100) {
          cardError.locationLabel =
            "Location Label must be 100 characters or less";
        }
      }

      if (card.suggestionType === "dialerAction") {
        if (!card.dialerPhoneNumber || card.dialerPhoneNumber.trim() === "") {
          cardError.dialerPhoneNumber = "Phone Number is required";
        } else if (!phonePattern.test(card.dialerPhoneNumber)) {
          cardError.dialerPhoneNumber =
            "Please enter a valid 10 digit number starting with either 6, 7, 8 or 9";
        } else if (repetitiveNumberPattern.test(card.dialerPhoneNumber)) {
          cardError.dialerPhoneNumber =
            "Repetitive phone numbers are not allowed";
        }
      }

      if (Object.keys(cardError).length > 0) {
        cardErrors[index] = cardError;
      }
    });

    if (Object.keys(cardErrors).length > 0) {
      newErrors.cards = cardErrors;
    }

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "campaignID") {
      if (form.campaignID === "" && value.trim() !== "") {
        const now = new Date();
        const hours = now.getHours();
        if (hours < 9 || hours >= 21) {
          CustomAlert({
            title: "Info",
            text: "RCS services are available only between 9 AM to 9 PM. Please try later.",
            icon: "info",
            confirmButtonText: "Okay",
            allowOutsideClick: false,
          });
          return;
        }
      }
      // Allow letters, numbers, spaces, underscore, hyphen
      const filteredValue = value.replace(/[^a-zA-Z0-9 _-]/g, "");
      setForm((prev) => ({ ...prev, [name]: filteredValue }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else if (name === "contacts") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 10) return;
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
      setCapabilityResult("");

      const cleanedValue = value.replace(/[^\d,]/g, "");
      const numbers = cleanedValue.split(",").map((num) => num.trim());
      const filteredNumbers = numbers.map((num) => num.slice(0, 10));

      const validNumbers = filteredNumbers.filter((num) =>
        /^[6-9]\d{9}$/.test(num)
      );
      const allVerified =
        validNumbers.length > 0 &&
        validNumbers.every((num) => verifiedPhones.includes(num));
      setIsVerified(allVerified);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCardChange = (index, field, value) => {
    const updated = [...form.cards];
    updated[index][field] = value;

    if (
      activeTab === "CAROUSEL_ADVANCED" &&
      field === "suggestionType" &&
      value === "showLocation"
    ) {
      fetchCurrentCoords((lat, lng) => {
        updated[index].locationLatitude = lat;
        updated[index].locationLongitude = lng;
        setForm({ ...form, cards: updated });
      });
    } else {
      setForm({ ...form, cards: updated });
    }
    setErrors((prev) => {
      const cardErrors = prev.cards?.[index] || {};
      return {
        ...prev,
        cards: {
          ...prev.cards,
          [index]: { ...cardErrors, [field]: "" },
        },
      };
    });
  };

  const handleBlur = (e, cardIndex = null) => {
    const { name, value } = e.target;
    const fieldErrors = validateField(name, value, cardIndex);
    setErrors((prev) => {
      if (cardIndex !== null) {
        const cardErrors = prev.cards ? { ...prev.cards } : {};
        cardErrors[cardIndex] = {
          ...(cardErrors[cardIndex] || {}),
          ...fieldErrors.cards?.[cardIndex],
        };
        return { ...prev, cards: cardErrors };
      }
      return { ...prev, ...fieldErrors };
    });
  };

  const addCard = () => {
    if (form.cards.length >= MAX_CARDS) {
      CustomAlert({
        title: "Limit Reached",
        text: `You can only add up to ${MAX_CARDS} cards.`,
        icon: "warning",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }
    console.log("Adding new card. Current card count:", form.cards.length);
    setForm({
      ...form,
      cards: [
        ...form.cards,
        {
          cardTitle: "",
          cardDescription: "",
          fileUrl: "",
          suggestionType:
            activeTab === "CAROUSEL" ? "openUrl" : "createCalendarEvent",
          buttonText: "",
          buttonUrl: "",
          calendarStartTime: "",
          calendarEndTime: "",
          calendarTitle: "",
          calendarDescription: "",
          locationLatitude: "",
          locationLongitude: "",
          locationLabel: "",
          dialerPhoneNumber: "",
          postBackData: "",
        },
      ],
    });
  };

  const removeCard = (index) => {
    const updated = [...form.cards];
    updated.splice(index, 1);
    setForm({ ...form, cards: updated });
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors.cards) {
        delete newErrors.cards[index];
      }
      return newErrors;
    });
  };

  const fetchDashboardData = () => {
    const endDate = formatDateToYYYYMMDD(new Date());
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);

    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
    };

    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          dispatch(setBalance(response?.data?.data?.balanceDetails));
          fetchData(1);
        } else if (response?.data?.respCode === "05") {
          CustomAlert({
            title: "Info",
            text:
              response?.data?.message ||
              "Invalid credentials. Please try again!",
            icon: "info",
            confirmButtonText: "Retry",
          });
        } else {
          // Handle error case here if necessary
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const sendRequest = async () => {
    const now = new Date();
    const hours = now.getHours();
    if (hours < 9 || hours >= 21) {
      CustomAlert({
        title: "Info",
        text: "RCS services are available only between 9 AM to 9 PM. Please try later.",
        icon: "info",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }
    if (!isVerified && !isBulkUpload) {
      CustomAlert({
        title: "Info",
        text: "Please select either Check, Bulk or Bulk Name before proceeding.",
        icon: "info",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }

    if (!validateForm()) {
      CustomAlert({
        title: "Error",
        text: "Please correct the errors in the form.",
        icon: "error",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }

    if (form.cards.length <= 1) {
      CustomAlert({
        title: "Minimum Cards Required",
        text: "You must have at least 2 cards.",
        icon: "warning",
        confirmButtonText: "Okay",
        allowOutsideClick: false,
      });
      return;
    }

    const payload = {
      username: loggedInUserName,
      campaignID: form.campaignID,
      contacts: form.contacts.split(",").map((c) => `+91${c.trim()}`),
      data: {
        Content: {
          richCardDetails: {
            carousel: {
              cardWidth: "MEDIUM_WIDTH",
              contents: form.cards.map((card) => {
                const suggestion = {
                  action: {
                    plainText: card.buttonText,
                    postBack: {
                      data:
                        activeTab === "CAROUSEL"
                          ? "call_back_data_for_button_1_goes_here{{$50}}"
                          : "SA1L1C1{{$50}}",
                    },
                  },
                };

                if (activeTab === "CAROUSEL") {
                  suggestion.action.openUrl = { url: card.buttonUrl };
                } else {
                  switch (card.suggestionType) {
                    case "createCalendarEvent":
                      suggestion.action.createCalendarEvent = {
                        startTime: new Date(
                          card.calendarStartTime
                        ).toISOString(),
                        endTime: new Date(card.calendarEndTime).toISOString(),
                        title: card.calendarTitle,
                        description: card.calendarDescription,
                      };
                      break;
                    case "showLocation":
                      suggestion.action.showLocation = {
                        coordinAtes: {
                          latitude: parseFloat(card.locationLatitude),
                          longitude: parseFloat(card.locationLongitude),
                        },
                        label: card.locationLabel,
                      };
                      break;
                    case "dialerAction":
                      suggestion.action.dialerAction = {
                        phoneNumber: `+91${card.dialerPhoneNumber}`,
                      };
                      break;
                    case "openUrl":
                      suggestion.action.openUrl = { url: card.buttonUrl };
                      break;
                    default:
                      break;
                  }
                }

                return {
                  cardTitle: card.cardTitle,
                  cardDescription: card.cardDescription,
                  cardMedia: {
                    mediaHeight: "MEDIUM",
                    contentInfo: {
                      fileUrl: card.fileUrl,
                    },
                  },
                  suggestions: [suggestion],
                };
              }),
            },
          },
        },
      },
    };

    if (isBulkUpload) {
      handleCsvBulkUpload(payload);
    } else {
      sendRcsCarousalApi(payload);
    }
  };

  const sendRcsCarousalApi = async (payload) => {
    try {
      const headers = {
        rcskey: rcsAgentId,
        "Content-Type": "application/json",
      };

      const apiEndpoint =
        activeTab === "CAROUSEL"
          ? apiList.SEND_CAROUSEL_MESSAGE
          : apiList.SEND_CAROUSEL_ADVANCED;

      const response = await axiosPostApi(apiEndpoint, payload, headers);
      if (response?.data?.meta?.status === "00") {
        CustomAlert({
          title: "Success",
          text: response?.data?.meta?.message,
          icon: "success",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        }).then(() => {
          fetchDashboardData();
        });
        console.log(
          "Resetting form. Previous calendarDescription:",
          form.cards[0].calendarDescription
        );
        setForm({
          username: "",
          campaignID: "",
          contacts: "",
          cards: [
            {
              cardTitle: "",
              cardDescription: "",
              fileUrl: "",
              suggestionType:
                activeTab === "CAROUSEL" ? "openUrl" : "createCalendarEvent",
              buttonText: "",
              buttonUrl: "",
              calendarStartTime: "",
              calendarEndTime: "",
              calendarTitle: "",
              calendarDescription: "",
              locationLatitude: "",
              locationLongitude: "",
              locationLabel: "",
              dialerPhoneNumber: "",
              postBackData: "",
            },
          ],
        });
        setErrors({});
        setIsVerified(false);
        setVerifiedPhones([]);
        setCapabilityResult("");
        setBulkType("");
        setIsNameRequired(false);
      } else {
        CustomAlert({
          title: "Error",
          text: response?.data?.meta?.message,
          icon: "error",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        });
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  const handleCsvBulkUpload = (payload) => {
    if (!csvFile) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        csv: "Please select a CSV file before uploading.",
      }));
      return;
    }
    const formDataA = new FormData();
    formDataA.append("username", loggedInUserName);
    formDataA.append("rcsType", activeTab);
    formDataA.append("rcsMessage", JSON.stringify(payload));
    formDataA.append("file", csvFile);
    // formDataA.append("isNameRequired", false);
    formDataA.append("isNameRequired", isNameRequired);

    try {
      bulkUpload(formDataA);
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const bulkUpload = async (payload) => {
    // setLoading(true);
    try {
      // Make the API call
      const response = await axiosPostApi(
        apiList.RCS_UPLOAD_MOBILE_EXCEL,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.status === 200 && response?.data?.respCode === "00") {
        CustomAlert({
          title: "Success",
          text: response?.data?.message,
          icon: "success",
          confirmButtonText: "Okay",
        }).then(() => {
          fetchDashboardData();
        });
        setForm({
          username: "",
          campaignID: "",
          contacts: "",
          cards: [
            {
              cardTitle: "",
              cardDescription: "",
              fileUrl: "",
              suggestionType:
                activeTab === "CAROUSEL" ? "openUrl" : "createCalendarEvent",
              buttonText: "",
              buttonUrl: "",
              calendarStartTime: "",
              calendarEndTime: "",
              calendarTitle: "",
              calendarDescription: "",
              locationLatitude: "",
              locationLongitude: "",
              locationLabel: "",
              dialerPhoneNumber: "",
              postBackData: "",
            },
          ],
        });
        setBulkFileName(null);
        setIsBulkUpload(false);
        setBulkType("");
        setIsNameRequired(false);
      } else if (response?.data?.respCode === "05") {
        CustomAlert({
          title: "Info",
          text:
            response?.data?.message || "Invalid credentials. Please try again!",
          icon: "info",
          confirmButtonText: "Retry",
        });
      } else {
        CustomAlert({
          title: "Data Fetch Error",
          text:
            response?.data?.message ||
            "No data available for the specified criteria.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: `There was an error: ${error?.message}`,
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      // setLoading(false);
    }
  };

  const checkCapability = async () => {
    if (isBulkUpload) return;

    const phone = form.contacts;

    if (!phone) {
      setCapabilityResult("⚠️ Please enter a valid phone number");
      return false;
    }

    setChecking(true);
    try {
      const headers = {
        rcskey: rcsAgentId,
        "Content-Type": "application/json",
      };

      const phoneNumbersArray = phone
        .split(",")
        .map((p) => `+91${p.trim()}`)
        .filter((p) => p.length > 3);

      const payload = {
        PhoneNumbers: phoneNumbersArray,
        username: loggedInUserName,
      };

      const response = await axiosPostApi(
        apiList.CHECK_CAPABILITY,
        payload,
        headers
      );

      if (response?.data?.meta?.status === "00" && response?.data?.data?.[0]) {
        setIsVerified(true);
        const newVerifiedNumbers = form.contacts
          .split(",")
          .map((num) => num.trim())
          .filter((num) => /^[6-9]\d{9}$/.test(num));
        setVerifiedPhones((prev) => [
          ...new Set([...prev, ...newVerifiedNumbers]),
        ]);
        CustomAlert({
          title: "Success",
          text: response?.data?.meta?.message,
          icon: "success",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        });
      } else {
        setIsVerified(false);
        CustomAlert({
          title: "Error",
          text: response?.data?.meta?.message,
          icon: "error",
          confirmButtonText: "Okay",
          allowOutsideClick: false,
        });
      }
    } catch (err) {
      console.error("checkCapability error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers,
      });
    } finally {
      setChecking(false);
    }
  };

  const disableBtn = isBulkUpload || isVerified;

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const fetchCurrentCoords = (cb) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        cb(latitude.toFixed(6), longitude.toFixed(6));
      },
      () => console.warn("Geolocation permission denied")
    );
  };

  return (
    <div
      style={{
        maxWidth: "1100px",
        padding: "24px",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      }}
    >
      <h2
        style={{ color: "#00b6CE", marginBottom: "16px", fontWeight: "bold" }}
      >
        {activeTab === "CAROUSEL" ? "Send RCS Carousel" : "RCS Suggested Reply"}
      </h2>
      <div className="scoped-modal modal-body">
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="form-group">
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Campaign ID
              </label>
              <input
                className="form-control"
                placeholder="Enter Campaign ID"
                name="campaignID"
                value={form.campaignID}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{
                  border: errors.campaignID
                    ? "1px solid red"
                    : "1px solid #ccc",
                }}
              />
              {errors.campaignID && (
                <span style={{ color: "red", fontSize: "14px" }}>
                  {errors.campaignID}
                </span>
              )}
            </div>
          </div>
          <div className="col-md-8">
            <div className="form-group">
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                Contacts
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <input
                  className="form-control"
                  placeholder="Enter phone number"
                  disabled={isBulkUpload}
                  name="contacts"
                  value={form.contacts}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{
                    backgroundColor: isBulkUpload ? "#e0e0e0" : "#f1f1f1",
                    border: errors.contacts
                      ? "1px solid red"
                      : "1px solid #ccc",
                  }}
                />
                <button
                  type="button"
                  onClick={checkCapability}
                  disabled={
                    checking ||
                    !rcsAgentId ||
                    disableBtn ||
                    (form.contacts &&
                      form.contacts
                        .split(",")
                        .map((num) => num.trim())
                        .filter((num) => /^[6-9]\d{9}$/.test(num))
                        .every((num) => verifiedPhones.includes(num)))
                  }
                  title={
                    form.contacts &&
                    form.contacts
                      .split(",")
                      .map((num) => num.trim())
                      .filter((num) => /^[6-9]\d{9}$/.test(num))
                      .every((num) => verifiedPhones.includes(num)) // Fixed: Changed | to =>
                      ? "All numbers already verified"
                      : "Check phone numbers"
                  }
                  style={{
                    padding: "8px 12px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: "#00BCD4",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "bold",
                    height: "40px",
                    cursor:
                      checking ||
                      !rcsAgentId ||
                      disableBtn ||
                      (form.contacts &&
                        form.contacts
                          .split(",")
                          .map((num) => num.trim())
                          .filter((num) => /^[6-9]\d{9}$/.test(num))
                          .every((num) => verifiedPhones.includes(num)))
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      checking ||
                      !rcsAgentId ||
                      disableBtn ||
                      (form.contacts &&
                        form.contacts
                          .split(",")
                          .map((num) => num.trim())
                          .filter((num) => /^[6-9]\d{9}$/.test(num))
                          .every((num) => verifiedPhones.includes(num)))
                        ? 0.6
                        : 1,
                  }}
                >
                  {/* {checking ? "Checking..." : "Check"} */}
                  {checking ? (
                    <CircularProgress
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    "Check"
                  )}
                </button>
                OR
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(true);
                    setBulkType("bulk");
                    setIsNameRequired(false);
                    setForm((prev) => ({ ...prev, contacts: "" }));
                    setErrors((prev) => ({ ...prev, contacts: "" }));
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#00BCD4",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "bold",
                    width: "90px",
                    height: "40px",
                    cursor: disableBtn ? "not-allowed" : "pointer",
                    opacity: disableBtn ? 0.6 : 1,
                  }}
                  disabled={disableBtn}
                >
                  Bulk
                </button>
                <span>OR</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(true);
                    setBulkType("name");
                    setIsNameRequired(true);
                    setForm((prev) => ({ ...prev, contacts: "" }));
                    setErrors((prev) => ({ ...prev, contacts: "" }));
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#00BCD4",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "bold",
                    minWidth: "120px",
                    height: "40px",
                    cursor: disableBtn ? "not-allowed" : "pointer",
                    opacity: disableBtn ? 0.6 : 1,
                  }}
                  disabled={disableBtn}
                >
                  Bulk Name
                </button>
              </div>
              {isVerified &&
              form.contacts
                .split(",")
                .map((num) => num.trim())
                .filter((num) => /^[6-9]\d{9}$/.test(num))
                .every((num) => verifiedPhones.includes(num)) ? (
                <span
                  style={{
                    marginTop: "6px",
                    color: "green",
                    fontSize: "14px",
                  }}
                >
                  verified
                </span>
              ) : (
                capabilityResult && (
                  <div
                    style={{
                      marginTop: "6px",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {capabilityResult}
                  </div>
                )
              )}
              {errors.contacts && (
                <span style={{ color: "red", fontSize: "14px" }}>
                  {errors.contacts}
                </span>
              )}
            </div>
          </div>
          <div className="col-md-6"></div>
          <div
            className="col-md-6"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "3px",
              visibility: !bulkFileName ? "hidden" : "",
            }}
          >
            {/* File name badge */}
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "40px",
                backgroundColor: "#e0f7fa",
                color: "#007c91",
                fontSize: "16px",
                maxWidth: "200px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                maxWidth: "300px",
                textOverflow: "ellipsis",
              }}
            >
              {bulkFileName?.name}
            </div>

            {/* Separate ❌ icon */}
            <button
              type="button"
              onClick={() => {
                setBulkFileName(null);
                setCsvFile(null);
                setIsBulkUpload(false);
                setIsVerified(false);
                setCapabilityResult("");
                setForm((prev) => ({
                  ...prev,
                  bulkFileName: "",
                }));
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#d32f2f",
                fontSize: "20px",
                cursor: "pointer",
                lineHeight: "1",
              }}
              title="Remove file"
            >
              ❌
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            overflow: "auto",
          }}
          className="custom-scrollbar"
        >
          {form.cards.map((card, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ccc",
                padding: "16px",
                borderRadius: "6px",
                backgroundColor: "#f9fafb",
                position: "relative",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      Card Title
                    </label>
                    <input
                      style={{
                        border: errors.cards?.[index]?.cardTitle
                          ? "1px solid red"
                          : "1px solid #ccc",
                      }}
                      className="form-control"
                      placeholder="Enter Card Title"
                      name="cardTitle"
                      value={card.cardTitle}
                      onChange={(e) =>
                        handleCardChange(index, "cardTitle", e.target.value)
                      }
                      onBlur={(e) => handleBlur(e, index)}
                    />
                    {errors.cards?.[index]?.cardTitle && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors.cards[index].cardTitle}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      Image URL
                    </label>
                    <input
                      style={{
                        border: errors.cards?.[index]?.fileUrl
                          ? "1px solid red"
                          : "1px solid #ccc",
                      }}
                      className="form-control"
                      placeholder="Enter Image URL"
                      name="fileUrl"
                      value={card.fileUrl}
                      onChange={(e) =>
                        handleCardChange(index, "fileUrl", e.target.value)
                      }
                      onBlur={(e) => handleBlur(e, index)}
                    />
                    {errors.cards?.[index]?.fileUrl && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors.cards[index].fileUrl}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="form-group">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        marginTop: "5px",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      Card Description
                    </label>
                    <textarea
                      style={{
                        border: errors.cards?.[index]?.cardDescription
                          ? "1px solid red"
                          : "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "4px",
                        gridColumn: "span 2",
                      }}
                      className="form-control"
                      placeholder="Enter Card Description"
                      name="cardDescription"
                      rows="3"
                      value={card.cardDescription}
                      onChange={(e) =>
                        handleCardChange(
                          index,
                          "cardDescription",
                          e.target.value
                        )
                      }
                      onBlur={(e) => handleBlur(e, index)}
                    />
                    {errors.cards?.[index]?.cardDescription && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors.cards[index].cardDescription}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        marginTop: "5px",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      Button Text
                    </label>
                    <input
                      style={{
                        border: errors.cards?.[index]?.buttonText
                          ? "1px solid red"
                          : "1px solid #ccc",
                      }}
                      className="form-control"
                      placeholder="Enter Button Text"
                      name="buttonText"
                      value={card.buttonText}
                      onChange={(e) =>
                        handleCardChange(index, "buttonText", e.target.value)
                      }
                      onBlur={(e) => handleBlur(e, index)}
                    />
                    {errors.cards?.[index]?.buttonText && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors.cards[index].buttonText}
                      </span>
                    )}
                  </div>
                </div>

                {activeTab === "CAROUSEL" ? (
                  <div className="col-md-6">
                    <div className="form-group">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          marginTop: "5px",
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        Button URL
                      </label>
                      <input
                        style={{
                          border: errors.cards?.[index]?.buttonUrl
                            ? "1px solid red"
                            : "1px solid #ccc",
                        }}
                        className="form-control"
                        placeholder="Enter Button URL"
                        name="buttonUrl"
                        value={card.buttonUrl}
                        onChange={(e) =>
                          handleCardChange(index, "buttonUrl", e.target.value)
                        }
                        onBlur={(e) => handleBlur(e, index)}
                      />
                      {errors.cards?.[index]?.buttonUrl && (
                        <span style={{ color: "red", fontSize: "14px" }}>
                          {errors.cards[index].buttonUrl}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            marginTop: "5px",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          Select Suggestions
                        </label>
                        <select
                          style={{
                            border: "1px solid #ccc",
                            appearance: "listbox",
                          }}
                          className="form-control"
                          value={card.suggestionType}
                          onChange={(e) =>
                            handleCardChange(
                              index,
                              "suggestionType",
                              e.target.value
                            )
                          }
                        >
                          <option value="createCalendarEvent">
                            Calendar Event
                          </option>
                          <option value="showLocation">Show Location</option>
                          <option value="dialerAction">Dialer Action</option>
                          <option value="openUrl">Open URL</option>
                        </select>
                      </div>
                    </div>
                    {card.suggestionType === "createCalendarEvent" && (
                      <>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Start Time
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.calendarStartTime
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              type="datetime-local"
                              placeholder="Enter Start Time"
                              name="calendarStartTime"
                              value={card.calendarStartTime}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "calendarStartTime",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.calendarStartTime && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].calendarStartTime}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              End Time
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.calendarEndTime
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              type="datetime-local"
                              placeholder="Enter End Time"
                              name="calendarEndTime"
                              value={card.calendarEndTime}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "calendarEndTime",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.calendarEndTime && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].calendarEndTime}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Calendar Title
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.calendarTitle
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              placeholder="Enter Calendar Title"
                              name="calendarTitle"
                              value={card.calendarTitle}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "calendarTitle",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.calendarTitle && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].calendarTitle}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Calendar Description
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]
                                  ?.calendarDescription
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              placeholder="Enter Calendar Description"
                              name="calendarDescription"
                              value={card.calendarDescription}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "calendarDescription",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.calendarDescription && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].calendarDescription}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    {card.suggestionType === "showLocation" && (
                      <>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Latitude
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.locationLatitude
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              placeholder="Enter Latitude"
                              name="locationLatitude"
                              value={card.locationLatitude}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "locationLatitude",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.locationLatitude && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].locationLatitude}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Longitude
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.locationLongitude
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              placeholder="Enter Longitude"
                              name="locationLongitude"
                              value={card.locationLongitude}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "locationLongitude",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.locationLongitude && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].locationLongitude}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "5px",
                                marginTop: "5px",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              Location Label
                            </label>
                            <input
                              style={{
                                border: errors.cards?.[index]?.locationLabel
                                  ? "1px solid red"
                                  : "1px solid #ccc",
                              }}
                              className="form-control"
                              placeholder="Enter Location Label"
                              name="locationLabel"
                              value={card.locationLabel}
                              onChange={(e) =>
                                handleCardChange(
                                  index,
                                  "locationLabel",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => handleBlur(e, index)}
                            />
                            {errors.cards?.[index]?.locationLabel && (
                              <span style={{ color: "red", fontSize: "14px" }}>
                                {errors.cards[index].locationLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    {card.suggestionType === "dialerAction" && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label
                            style={{
                              display: "block",
                              marginBottom: "5px",
                              marginTop: "5px",
                              fontWeight: "bold",
                              fontSize: "14px",
                            }}
                          >
                            Phone Number
                          </label>
                          <input
                            style={{
                              border: errors.cards?.[index]?.dialerPhoneNumber
                                ? "1px solid red"
                                : "1px solid #ccc",
                            }}
                            className="form-control"
                            placeholder="Enter Phone Number"
                            name="dialerPhoneNumber"
                            value={card.dialerPhoneNumber}
                            onChange={(e) =>
                              handleCardChange(
                                index,
                                "dialerPhoneNumber",
                                e.target.value
                              )
                            }
                            onBlur={(e) => handleBlur(e, index)}
                          />
                          {errors.cards?.[index]?.dialerPhoneNumber && (
                            <span style={{ color: "red", fontSize: "14px" }}>
                              {errors.cards[index].dialerPhoneNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {card.suggestionType === "openUrl" && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label
                            style={{
                              display: "block",
                              marginBottom: "5px",
                              marginTop: "5px",
                              fontWeight: "bold",
                              fontSize: "14px",
                            }}
                          >
                            Button URL
                          </label>
                          <input
                            style={{
                              border: errors.cards?.[index]?.buttonUrl
                                ? "1px solid red"
                                : "1px solid #ccc",
                            }}
                            className="form-control"
                            placeholder="Enter Button URL"
                            name="buttonUrl"
                            value={card.buttonUrl}
                            onChange={(e) =>
                              handleCardChange(
                                index,
                                "buttonUrl",
                                e.target.value
                              )
                            }
                            onBlur={(e) => handleBlur(e, index)}
                          />
                          {errors.cards?.[index]?.buttonUrl && (
                            <span style={{ color: "red", fontSize: "14px" }}>
                              {errors.cards[index].buttonUrl}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {form.cards.length > 1 && (
                <button
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    color: "red",
                    border: "none",
                    background: "none",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  onClick={() => removeCard(index)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <button
          onClick={addCard}
          style={{
            padding: "8px 16px",
            backgroundColor: "#3b82f6",
            color: "white",
            borderRadius: "4px",
            border: "none",
            cursor: form.cards.length >= MAX_CARDS ? "not-allowed" : "pointer",
            opacity: form.cards.length >= MAX_CARDS ? 0.6 : 1,
          }}
          disabled={form.cards.length >= MAX_CARDS}
        >
          + Add Card
        </button>
        <button
          onClick={sendRequest}
          type="button"
          className="btn btn-submit"
          style={{ width: "90px" }}
        >
          Send
        </button>
      </div>
      <RcsBulkMessageModal
        show={showModal}
        handleClose={handleCloseModal}
        activeTab={activeTab}
        setIsBulkUpload={setIsBulkUpload}
        setBulkFileName={setBulkFileName}
        setCsvFile={setCsvFile}
        csvFile={csvFile}
        bulkType={bulkType}
      />
    </div>
  );
}
